import React, { ReactElement } from 'react';
import { MenuItem } from './MenuItem';

export interface IDropdownButtonProps {
  /**
   * used to create a bootstrap btn class ( e.g. btn-default)
   * @default default;
   */
  bsStyle?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'cancel'
    | 'link';
  /**
   * size of the button
   * @default sm
   */
  bsSize?: 'lg' | 'sm' | 'xs';
  /**
   * default is 185
   * @default 185
   */
  dropdownListWidth?: number;
  /**
   * the text that appears in the button.  this is ignored for ellipsis.
   */
  title?: string;
  /**
   * when true do not render a caret icon
   * @default false
   */
  noCaret?: boolean;
  /**
   * align the dropdown menu bottom-right or bottom-left
   * @default 'left'
   */
  align?: 'right' | 'left';
  /**
   * button disabled attribute
   * @default false
   */
  disabled?: boolean;
  /**
   * This is a shortcut for rendering an ellipsis dropdown button.
   * When true uses ellipsis icon and some custom hover styling.
   * NOTE:  props.title is ignored when ellipsis is true
   * @default false;
   */
  ellipsis?: boolean;
  /**
   * max height of the dropdown container
   * @default 200
   */
  maxHeight?: number;
  /**
   * By default, when a MenuItem with an eventKey is "selected" the dropdown closes and focus is
   * returned to the DropdownButtonTrigger.  Sometimes that behavior is not desired because some other
   * component wants to assign the focus somewhere else.  Setting this prop to true releases control of focus
   * for ALL MenuItem clicks.  To release focus for a single MenuItem place this prop on the MenuItem.
   * @default true
   */
  releaseFocusAfterSelect?: boolean;
  /**
   * a renderer for the trigger button if you need to customize it somehow
   */
  // tslint:disable-next-line
  triggerRenderer?: (props: IDropdownTriggerProps) => ReactElement<any>;

  /**
   * class names to put on the dropdown menu to customized it
   */
  // tslint:disable-next-line
  menuClassNames?: string;

  /**
   * Helper attribute for QA. Adds "qa-id" attribute to component's container
   */
  'qa-id'?: string;
  /**
   * callback when MenuItem that has an eventKey is clicked
   */
  onSelect?: (eventKey: any) => void; // tslint:disable-line
  /**
   * children are MenuItem components
   */
  children:
    | React.ReactElement<MenuItem>
    | React.ReactNode
    | (React.ReactElement<MenuItem> | React.ReactNode)[];
  /**
   * Content to render inside the info portion of dropdown
   */
  renderInfo?(): ReactElement<any>;
}
export interface IDropdownTriggerProps {
  title?: string;
  noCaret?: boolean;
  id: string;
  className: string;
  disabled?: boolean;
  'aria-haspopup': boolean;
  'aria-expanded': boolean;
  'aria-activedescendant'?: string;
  ref?: (e: any) => void;
  role?: string;
  /**
   * @default 0
   */
  tabIndex: number;
  onClick: (e: React.MouseEvent<any>) => void; // tslint:disable-line
}

export interface IMenuItemProps {
  /**
   * @default 'dropdown'
   * @ignore
   */
  bsClass?: string;
  /**
   * @ignore
   */
  className?: string;

  /**
   * Highlight the menu item as active.
   * @ignore
   */
  active?: boolean;
  /**
   * Disable the menu item, making it unselectable.
   * @default false
   */
  disabled?: boolean;

  /**
   * Styles the menu item as a horizontal rule, providing visual separation between
   * groups of menu items.
   * @default false
   */
  divider?: boolean;

  /**
   * Value passed to the `onSelect` handler, useful for identifying the selected menu item.
   */
  eventKey?: any; // tslint:disable-line

  /**
   * Styles the menu item as a header label, useful for describing a group of menu items.
   * @default false
   */
  header?: boolean;

  /**
   * HTML `href` attribute corresponding to `a.href`.
   */
  href?: string;

  /**
   * @ignore
   */
  id?: string;

  /**
   * By default, when a MenuItem with an eventKey is "selected" the dropdown closes and focus is
   * returned to the DropdownButtonTrigger.  Sometimes that behavior is not desired because some other
   * component wants to assign the focus elsewhere.  Setting this prop to true releases control of focus.
   * @default true
   */
  releaseFocusAfterSelect?: boolean;
  /**
   * target attr for anchor tag
   */
  target?: string;

  /**
   * Callback fired when the menu item is clicked.
   */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;

  /**
   * Callback fired when the a menu item that has an eventKey is clicked.
   */
  onSelect?: (eventKey: any, e: React.MouseEvent<HTMLElement>) => void; // tslint:disable-line
}

export interface IDropdownListProps {
  className?: string;
  align: 'right' | 'left';
  elContainer: HTMLDivElement | null;
  height: number;
  labeledById: string;
  width: number;
  onClose: () => void;
  onSelect?: (eventKey: any) => void; // tslint:disable-line
  renderInfo?(): ReactElement<any>;
}
